import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { getActivePianificazioni, getImagesByPianificazione } from "../../../lib/api";
import { API_BASE } from "../../../lib/apiHelper";
import { devError } from "../../../utils/devConsole";

const ADAPTIVE_COVER_RATIO_THRESHOLD = 2.2; // >= this ratio: prefer cover (no side bands)

export default function Carosello() {
  const settings = {
    autoplay: true,
    autoplaySpeed: 3000,
    dots: false,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1600,
        settings: { slidesToShow: 1 },
      },
      {
        breakpoint: 800,
        settings: { slidesToShow: 1 },
      },
      {
        breakpoint: 600,
        settings: { slidesToShow: 1 },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  const [images, setImages] = useState([]);
  const [ratiosById, setRatiosById] = useState({});

  useEffect(() => {
    getImagesOrdered();
  }, []);

  async function getImagesOrdered() {
    try {
      const activePlans = await getActivePianificazioni();
      if (!activePlans || activePlans.length === 0) {
        setImages([]);
        return;
      }

      const imagesByPlan = await Promise.all(
        activePlans.map(async (plan) => {
          const planImages = await getImagesByPianificazione(plan.pianificazione_id);
          const filtered = planImages.filter(
            (img) => typeof img.Ordine === "number" && img.Ordine >= 1 && img.Ordine <= 6
          );
          return filtered.map((img) => ({
            ...img,
            _planOrder: plan.Numero ?? 0,
          }));
        })
      );

      const mergedImages = imagesByPlan
        .flat()
        .sort((a, b) => (a._planOrder - b._planOrder) || (a.Ordine - b.Ordine));

      setImages(mergedImages);
    } catch (error) {
      devError("Error fetching images:", error);
    }
  }

  // Function to construct the image URL
  const buildImageSrc = (filePath) => {
    const baseUrl = API_BASE.replace(/\/$/, ''); // rimuove lo slash finale se presente
    let cleanedPath = filePath?.replace(/\\/g, '/') || '';
    if (cleanedPath.startsWith('/')) cleanedPath = cleanedPath.substring(1); // rimuove lo slash iniziale
    // Aggiunge il prefisso 'uploads/' se non è già presente
    const finalPath = cleanedPath.startsWith('uploads/') ? cleanedPath : `uploads/${cleanedPath}`;
    return `${baseUrl}/${finalPath}?${new Date().getTime()}`;
  };

  const formatExternalLink = (link) => {
    if (!link) return '#';
    // Force the HTTP protocol if missing
    return link.startsWith('http') ? link : `http://${link}`;
  };

  const storeRatio = (id, width, height) => {
    if (!id || !width || !height) return;
    const ratio = width / height;
    setRatiosById((prev) => {
      const existing = prev[id];
      if (existing && Math.abs(existing - ratio) < 0.01) return prev;
      return { ...prev, [id]: ratio };
    });
  };

  const modeFor = (id) => {
    const ratio = ratiosById[id];
    if (ratio !== undefined && ratio >= ADAPTIVE_COVER_RATIO_THRESHOLD) return "cover";
    return "contain-blur";
  };

  return (
    <>
      {images.length > 0 && (
        <div id="Carosello" className="peopleSayBgColor bg-white">
          <div className="container bg-white">
            {images.length > 1 ? (
              <Slider {...settings}>
                {images.map((image) => (
                  <div key={image.immagine_id}>
                    {image.Link ? (
                      <a 
                        href={formatExternalLink(image.Link)}
                        target="_blank" // Always open in a new tab
                        rel="noopener noreferrer"
                      >
                        {modeFor(image.immagine_id) === "cover" ? (
                          <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                            <img
                              className="w-full h-full object-cover object-center"
                              alt={image.Nome || "Immagine"}
                              src={buildImageSrc(image.File)}
                              onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                            />
                          </div>
                        ) : (
                          <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                            <img
                              src={buildImageSrc(image.File)}
                              alt=""
                              aria-hidden="true"
                              className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                            />
                            <img
                              className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                              alt={image.Nome || "Immagine"}
                              src={buildImageSrc(image.File)}
                              onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                            />
                          </div>
                        )}
                      </a>
                    ) : (
                      modeFor(image.immagine_id) === "cover" ? (
                        <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                          <img
                            className="w-full h-full object-cover object-center"
                            alt={image.Nome || "Immagine"}
                            src={buildImageSrc(image.File)}
                            onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                          />
                        </div>
                      ) : (
                        <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                          <img
                            src={buildImageSrc(image.File)}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                          />
                          <img
                            className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                            alt={image.Nome || "Immagine"}
                            src={buildImageSrc(image.File)}
                            onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                          />
                        </div>
                      )
                    )}
                  </div>
                ))}
              </Slider>
            ) : (
              images.map((image) => (
                <div key={image.immagine_id}>
                  {image.Link ? (
                    <a 
                      href={formatExternalLink(image.Link)}
                      target="_blank" // Always open in a new tab
                      rel="noopener noreferrer"
                    >
                      {modeFor(image.immagine_id) === "cover" ? (
                        <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                          <img
                            className="w-full h-full object-cover object-center"
                            alt={image.Nome || "Immagine"}
                            src={buildImageSrc(image.File)}
                            onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                          />
                        </div>
                      ) : (
                        <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                          <img
                            src={buildImageSrc(image.File)}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                          />
                          <img
                            className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                            alt={image.Nome || "Immagine"}
                            src={buildImageSrc(image.File)}
                            onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                          />
                        </div>
                      )}
                    </a>
                  ) : (
                    modeFor(image.immagine_id) === "cover" ? (
                      <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                        <img
                          className="w-full h-full object-cover object-center"
                          alt={image.Nome || "Immagine"}
                          src={buildImageSrc(image.File)}
                          onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                        />
                      </div>
                    ) : (
                      <div className="relative overflow-hidden bg-white h-[240px] sm:h-[300px] md:h-[360px] lg:h-[420px]">
                        <img
                          src={buildImageSrc(image.File)}
                          alt=""
                          aria-hidden="true"
                          className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
                        />
                        <img
                          className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
                          alt={image.Nome || "Immagine"}
                          src={buildImageSrc(image.File)}
                          onLoad={(e) => storeRatio(image.immagine_id, e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
                        />
                      </div>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
}
